// fallow-ignore-file complexity -- pre-existing CTA decision tree, moved verbatim from the delivery-mode banner
import { useMemo } from 'react';
import { useStore } from '@nanostores/react';
import { useRouter } from '@tanstack/react-router';
import { isSendingDisabled, isSendingVerified, type Domain } from '@/services/db/domain/entity';
import { useDomainsOptional, type DomainsList } from '@/services/db/domain/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useMailServersOptional } from '@/services/db/mail-server/queries';
import { generatePath } from '@/services/router/generate-path';
import { AccountState, VerificationReviewState, VerificationState } from '@/services/auth/types';
import { authStore } from '@/services/auth/store';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useHasRepliedToVerification } from '@/features/onboarding';
import { DeliverySettings, useSendTransactionalStep } from '@/features/workspaces';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';
import {
  CTA_COPY,
  SENDING_DOMAINS_ROUTE,
  WORKSPACE_HOME_ROUTE,
  WORKSPACE_SETTINGS_EDIT_ROUTE,
  type DeliveryModeCtaCopy,
} from './cta';
import { sendsAllEmailThroughCustomSmtp } from './custom-smtp';

// Module-scope so the function reference is stable across renders —
// `useQuery`'s `select` memoization keys on it, and a fresh closure
// each render would defeat the structural-sharing optimization.
function selectVerifiedDomains(data: DomainsList): readonly Domain[] {
  // isSendingVerified: a disabled domain would suppress the "Add a sending
  // domain" prompt while the workspace still cannot send.
  return data.domains.filter((d) => isSendingVerified(d));
}

// A domain the customer has added that is still waiting on DNS. Unlike the
// "any domain" test this replaces, a verified-but-disabled domain does not
// count: it is already verified. (`isSendingDisabled` is excluded for the same
// reason Builder setup excludes it from its pending list.)
function selectHasUnverifiedDomain(data: DomainsList): boolean {
  return data.domains.some((d) => d.verified !== true && !isSendingDisabled(d));
}

/**
 * Everything the delivery-mode popover and banner decide from: the workspace's delivery mode, plan
 * and verification state, the sending-domain / From-address set-up state, and the call to action
 * that follows from them.
 */
export function useDeliveryModeState() {
  const auth = useStore(authStore);
  const router = useRouter();

  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? 0;

  const sendingDomainsHref = router.buildLocation({
    to: '/workspaces/$workspaceId/settings/actions/email/sending_domains',
    params: { workspaceId },
  }).href;

  const buildCtaHref = (ctaCopy: DeliveryModeCtaCopy) => {
    if (ctaCopy.route == null) {
      return '#';
    }

    if (ctaCopy.route === WORKSPACE_SETTINGS_EDIT_ROUTE) {
      return router.buildLocation({
        to: '/workspaces/$workspaceId/settings/edit',
        params: { workspaceId },
        search: ctaCopy.query ?? {},
      }).href;
    }

    if (ctaCopy.route === WORKSPACE_HOME_ROUTE) {
      return router.buildLocation({
        to: '/workspaces/$workspaceId/home',
        params: { workspaceId },
        search: ctaCopy.query ?? {},
      }).href;
    }

    if (ctaCopy.route === SENDING_DOMAINS_ROUTE) {
      return router.buildLocation({
        to: '/workspaces/$workspaceId/settings/actions/email/sending_domains',
        params: { workspaceId },
        search: ctaCopy.query ?? {},
      }).href;
    }

    return generatePath(ctaCopy.route, { workspaceId }, ctaCopy.query);
  };

  // Pass the current workspace id explicitly: the banner renders in the layout
  // chrome on non-numeric routes (e.g. /workspaces/last/...) where the URL-only
  // lookup would otherwise skip and leave setup copy wrong.
  const { data: environmentData } = useEnvironmentOptional(workspace?.id);

  const env = environmentData?.environment ?? null;

  const isFromAddressSetUp = useMemo(() => {
    const identityIds = new Set(env?.identity_ids ?? []);
    const filtered = (environmentData?.identities ?? []).filter((identity) => identityIds.has(identity.id));
    return filtered.length > 0;
  }, [env?.identity_ids, environmentData?.identities]);

  const isAccountVerificationDisabled = useFeatureFlag(FeatureFlag.AccountVerification);

  const { data: verifiedDomains } = useDomainsOptional({ select: selectVerifiedDomains });
  const isSendingDomainSetUp = verifiedDomains != null && verifiedDomains.length > 0;
  const { data: hasUnverifiedDomain } = useDomainsOptional({ select: selectHasUnverifiedDomain });

  const isTestDeliveryMode = workspace?.delivery_settings === DeliverySettings.SendToOne;
  const isDeliveryDisabled = workspace?.delivery_settings === DeliverySettings.Off;

  const isBuilder = isBuilderPlan(auth.account);
  const builderMustUpgrade = isBuilder && auth.account?.account_state === AccountState.Free;

  // A workspace that sends all email through custom SMTP needs no sending
  // domain. Only the non-Builder test-mode copy asks for one, so only that
  // case reads the servers. While the read is in flight or after it fails,
  // the copy keeps the domain prompt, as it did before it read the servers.
  const { data: mailServers } = useMailServersOptional({
    workspaceId: workspace?.id,
    enabled: isTestDeliveryMode && !isBuilder,
  });
  const usesCustomSmtp = sendsAllEmailThroughCustomSmtp(mailServers ?? [], auth.account?.custom_smtp_locked === true);
  const isSendingDomainStepDone = isSendingDomainSetUp || usesCustomSmtp;
  // Added but not yet verified: the next step is finishing DNS, not adding another
  // domain. A verified-but-disabled domain is not pending verification — its next
  // step is turning sending back on — so it keeps the add-domain copy instead.
  const isSendingDomainAddedUnverified = !isSendingDomainStepDone && hasUnverifiedDomain === true;
  const { hasSentTransactional } = useSendTransactionalStep(isBuilder, workspace?.id ?? 0);

  const isAccountVerified = useMemo(() => {
    return auth.account?.verification_state === VerificationState.Approved;
  }, [auth?.account?.verification_state]);

  const isAccountVerificationPending = useMemo(() => {
    return auth.account?.verification_state === VerificationState.Pending;
  }, [auth?.account?.verification_state]);

  const hasRepliedToVerification = useHasRepliedToVerification();
  const verificationNeedsMoreInfo =
    auth.account?.verification_review_state === VerificationReviewState.NeedsMoreInfo && !hasRepliedToVerification;
  const verificationRejected = auth.account?.verification_state === VerificationState.Rejected;

  const cta = useMemo<DeliveryModeCtaCopy | null>(() => {
    if (isBuilder) {
      if (builderMustUpgrade) {
        return CTA_COPY.builderCompleteSetup;
      }
      if (isTestDeliveryMode) {
        return CTA_COPY.builderCompleteSetup;
      }
      // Builder paid + delivery off falls through to the regular path below.
    }

    if (!isAccountVerified) {
      // When the AccountVerification flag is on, the user can't manually verify —
      // verification status outranks domain-setup CTAs, since domain work can't
      // get messages delivered while the account is under review.
      if (isAccountVerificationDisabled) {
        if (isDeliveryDisabled) {
          return CTA_COPY.messageSendingSettings;
        }
        if (verificationRejected) {
          return CTA_COPY.accountVerificationRejected;
        }
        return CTA_COPY.accountVerificationPending;
      }

      if (verificationRejected) {
        return CTA_COPY.accountVerificationRejected;
      }
      if (verificationNeedsMoreInfo) {
        return CTA_COPY.accountVerificationPending;
      }

      if (isDeliveryDisabled) {
        return CTA_COPY.deliverySettingsDisabledUnverified;
      } else if (isSendingDomainAddedUnverified) {
        return CTA_COPY.sendingDomainPendingVerificationUnverified;
      } else if (!isSendingDomainStepDone) {
        return CTA_COPY.sendingDomainNotSetUpUnverified;
      } else if (!isFromAddressSetUp) {
        return CTA_COPY.fromAddressNotSetUpUnverified;
      } else if (isAccountVerificationPending) {
        return CTA_COPY.accountVerificationPending;
      } else {
        return CTA_COPY.verifyAccount;
      }
    } else {
      if (isDeliveryDisabled) {
        return CTA_COPY.messageSendingSettings;
      } else if (isSendingDomainAddedUnverified) {
        return CTA_COPY.sendingDomainPendingVerification;
      } else if (!isSendingDomainStepDone) {
        return CTA_COPY.sendingDomainNotSetUp;
      } else if (!isFromAddressSetUp) {
        return CTA_COPY.fromAddressNotSetUp;
      } else if (isTestDeliveryMode) {
        return CTA_COPY.deliverySettingsSendToOne;
      }
    }
    return null;
  }, [
    isAccountVerified,
    isAccountVerificationDisabled,
    isAccountVerificationPending,
    isDeliveryDisabled,
    isSendingDomainStepDone,
    isSendingDomainAddedUnverified,
    isFromAddressSetUp,
    isTestDeliveryMode,
    isBuilder,
    builderMustUpgrade,
    verificationRejected,
    verificationNeedsMoreInfo,
  ]);

  return {
    workspace,
    isBuilder,
    builderMustUpgrade,
    isTestDeliveryMode,
    isDeliveryDisabled,
    isSendingDomainSetUp,
    isSendingDomainStepDone,
    isSendingDomainAddedUnverified,
    isFromAddressSetUp,
    hasSentTransactional,
    verificationRejected,
    verificationNeedsMoreInfo,
    sendingDomainsHref,
    cta,
    buildCtaHref,
  };
}
